"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.activitySchemaProcessed = exports.activitySchema = void 0;
const zod_1 = require("zod");
const date_1 = require("../../date/date");
const zodCommon_1 = require("../zodCommon");
const attendeeSchema = zod_1.z
    .object({
    documents: zodCommon_1.documentsTransformer.optional(),
    users: zod_1.z.array(zodCommon_1.userSchema).optional(),
    notes: zod_1.z.string().trim().optional(),
    _id: zod_1.z.string().optional(),
})
    .catchall(zod_1.z.any())
    .optional()
    .nullable();
const taskSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(1, 'Required').max(100),
})
    .catchall(zod_1.z.any());
// Common fields both schemas will share
const commonFields = {
    visibility: zod_1.z.string().optional(),
    status: zod_1.z.string().optional(),
    attendees_list: zod_1.z.array(attendeeSchema).optional(),
};
// Schema for "todo" type activities
const todoSchema = zod_1.z
    .object({
    type: zod_1.z.literal('todo'),
    todo_name: zod_1.z
        .string()
        .trim()
        .min(1, 'Required')
        .max(100, 'Only 100 characters are allowed'),
    todo_description: zod_1.z
        .string()
        .trim()
        .max(1000, 'Only 100 characters are allowed')
        .optional(),
    due_date: zod_1.z.string().optional().nullable(),
    start_by: zod_1.z.string().optional().nullable(),
    tasks: zod_1.z.array(taskSchema),
    ...commonFields,
})
    .catchall(zod_1.z.any());
// Schema for "general" type activities
const generalNoType = {
    activity_name: zod_1.z
        .string()
        .trim()
        .min(1, 'Required')
        .max(100, 'Only 100 characters are allowed'),
    location: zod_1.z.string().trim().optional(),
    categories: zod_1.z.array(zod_1.z.string()).optional(),
    start: zod_1.z.string().min(1, 'Required'),
    end: zod_1.z.string().optional().nullable(),
    check_in: zod_1.z.string().optional(),
    address: zod_1.z.any().optional(),
    additional_information: zod_1.z.string().trim().optional(),
    website: zod_1.z.string().trim().optional(),
    description: zod_1.z.string().trim().optional(),
    cardImages: zod_1.z.array(zodCommon_1.imageSchema).optional(),
    images: zod_1.z.array(zodCommon_1.imageSchema).optional(),
    documents: zodCommon_1.documentsTransformer,
    ...commonFields,
};
// Schema for "stand" type activities
const standType = {
    activity_name: zod_1.z
        .string()
        .trim()
        .min(1, 'Required')
        .max(100, 'Only 100 characters are allowed'),
    location: zod_1.z.string().trim().optional(),
    categories: zod_1.z.array(zod_1.z.string()).optional(),
    address: zod_1.z.any().optional(),
    additional_information: zod_1.z.string().trim().optional(),
    website: zod_1.z.string().trim().optional(),
    description: zod_1.z.string().trim().optional(),
    images: zod_1.z.array(zodCommon_1.imageSchema).optional(),
    cardImages: zod_1.z.array(zodCommon_1.imageSchema).optional(),
    documents: zodCommon_1.documentsTransformer,
    visibility: zod_1.z.string().optional(),
    status: zod_1.z.string().optional(),
};
const standSchema = zod_1.z
    .object({
    type: zod_1.z.literal('stand'),
    ...standType,
})
    .catchall(zod_1.z.any());
const generalSchema = zod_1.z
    .object({
    type: zod_1.z.literal('general'),
    ...generalNoType,
})
    .catchall(zod_1.z.any());
const undefinedSchema = zod_1.z
    .object({
    type: zod_1.z.literal(undefined),
    ...generalNoType,
})
    .catchall(zod_1.z.any());
exports.activitySchema = zod_1.z
    .discriminatedUnion('type', [
    todoSchema,
    generalSchema,
    undefinedSchema,
    standSchema,
])
    .refine((data) => {
    if (!data.end)
        return true;
    const startDate = new date_1.EventrDate(data.start).getUnixMs;
    const endDate = new date_1.EventrDate(data.end).getUnixMs;
    return endDate >= startDate;
}, {
    message: 'End date must be after start date',
    path: ['end'],
});
// this function to clean up the data before validation
exports.activitySchemaProcessed = zod_1.z.preprocess((data) => {
    // Only process if it's an object with a type and tasks field
    if (typeof data === 'object' && data !== null) {
        if (data.images && data.images.length > 0) {
            // remove images that have no url
            data.images = data.images.filter((image) => image?.url && image.url.trim() !== '');
        }
        if (data.cardImages && data.cardImages.length > 0) {
            // remove images that have no url
            data.cardImages = data.cardImages.filter((image) => image?.url && image.url.trim() !== '');
        }
        // Only process todo type activities
        if (data.type === 'todo' && Array.isArray(data.tasks)) {
            // Special case: if there's only one task and it's empty, don't modify it
            // This allows the validation to catch the empty task error
            if (data.tasks.length === 1) {
                const name = data.tasks[0].name;
                if (!name || name.trim() === '')
                    return data;
            }
            // Filter out tasks with empty names
            const filteredTasks = data.tasks.filter((task) => task && task.name && task.name.trim() !== '');
            if (filteredTasks.length === 0) {
                data.tasks = [{ name: '', _id: '' }];
            }
            else {
                data.tasks = filteredTasks;
            }
        }
    }
    // Return unchanged for non-todo items or if tasks isn't an array
    return data;
}, exports.activitySchema);
